import { useContext } from "react";

import { useMediaQuery } from "@uidotdev/usehooks";

import {
  getDevice,
  isGreaterThanScreen,
  isLowerThanScreen,
  isScreen,
} from "./responsive";
import { Device, ScreenValue } from "./types";
import { ResponsiveContext } from "./responsiveContext";
import { MediaQuery } from "./mediaQuery";

export interface UseResponsiveValue extends ScreenValue {
  device: Device;
  is: (device: Device) => boolean;
  isGreaterThanLaptop: boolean;
  isGreaterThanMobile: boolean;
  isGreaterThanTablet: boolean;
  isGreaterThanDesktop: boolean;
  isGreaterThanWide: boolean;
  isLowerThanLaptop: boolean;
  isLowerThanWide: boolean;
  isLowerThanTablet: boolean;
  isLowerThanDesktop: boolean;
  isLowerThanUltraWide: boolean;
  isLargeMobile: boolean;
}

export const useWebResponsive = (): UseResponsiveValue => {
  const isMobile = useMediaQuery(MediaQuery.mobile);

  const isLargeMobile = useMediaQuery(MediaQuery.largeMobile);

  const isTablet = useMediaQuery(MediaQuery.tablet);

  const isLaptop = useMediaQuery(MediaQuery.laptop);

  const isDesktop = useMediaQuery(MediaQuery.desktop);

  const isWide = useMediaQuery(MediaQuery.wide);

  const isUltraWide = useMediaQuery(MediaQuery.ultraWide);

  const screenValues = {
    isDesktop,
    isLaptop,
    isLargeMobile,
    isMobile,
    isTablet,
    isUltraWide,
    isWide,
  };

  const is = (device: Device) => isScreen(device, screenValues);

  const isGreaterThan = (device: Omit<Device, "wide">) =>
    isGreaterThanScreen(device, screenValues);

  const isLowerThan = (device: Omit<Device, "mobile">) =>
    isLowerThanScreen(device, screenValues);

  return {
    device: getDevice({
      isDesktop,
      isLaptop,
      isMobile,
      isTablet,
      isUltraWide,
      isWide,
    }),
    is,
    isDesktop,
    isGreaterThanDesktop: isGreaterThan("desktop"),
    isGreaterThanLaptop: isGreaterThan("laptop"),
    isGreaterThanMobile: isGreaterThan("mobile"),
    isGreaterThanTablet: isGreaterThan("tablet"),
    isGreaterThanWide: isGreaterThan("wide"),
    isLaptop,
    isLargeMobile,
    isLowerThanDesktop: isLowerThan("desktop"),
    isLowerThanLaptop: isLowerThan("laptop"),
    isLowerThanTablet: isLowerThan("tablet"),
    isLowerThanUltraWide: isLowerThan("ultraWide"),
    isLowerThanWide: isLowerThan("wide"),
    isMobile,
    isTablet,
    isUltraWide,
    isWide,
  };
};

export const useResponsive = (): UseResponsiveValue => {
  const values = useContext<UseResponsiveValue>(ResponsiveContext);

  return values;
};
